import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation, useSearchParams, useNavigate, matchPath } from "react-router-dom";
import { Menu, X, LogOut, Home, Bell, ListChecks, Building2, LayoutGrid, FileText, Settings, MessagesSquare } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { usePartners, useBranches, useProfile, useNotifications, useTasks } from "../hooks/queries";
import { UserAvatar } from "./ui/user-avatar";
import { SectionLabel } from "./ui/section-label";
import { getEnvironment } from "../lib/env";
import { partnerIconUrl } from "../lib/partner";
import { cn } from "@/lib/utils";
import { useSettingsDialog } from "./SettingsDialog";
import { WavesLogo } from "./WavesLogo";
import NotificationBell from "./NotificationBell";
import TasksBell from "./TasksBell";
import HelpButton from "./help/HelpButton";
import type { Partner } from "../api/api";

const environment = getEnvironment();

interface LayoutProps {
  children: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  noPadding?: boolean;
  /** Hero pages (dashboards): no title row — the page renders its own hero, and
   *  the Meldingen/Taken buttons float top-right over the content. */
  bare?: boolean;
}

const roleSubtitle: Record<string, string> = {
  admin:         "Beheerder · Waves",
  kam:           "Key account manager · Waves",
  hmp:           "Hiring manager",
  partner_admin: "Partner admin",
};

function pageTitleFromPath(pathname: string): string {
  if (pathname === "/") return "Dashboard";
  if (pathname.startsWith("/vacatures")) return "Vacatures";
  if (pathname === "/partnerbeheer") return "Partnerbeheer";
  if (pathname === "/audit-trail") return "Audit trail";
  if (pathname === "/gebruikersbeheer") return "Gebruikersbeheer";
  if (pathname === "/alert-regels") return "Alertering";
  if (pathname === "/systeemstatus") return "Systeemstatus";
  if (pathname === "/functietitels") return "Functiesuggesties";
  if (pathname === "/beheer") return "Beheer";
  if (pathname === "/mail-goedkeuring") return "Mail goedkeuring";
  if (pathname === "/toegangsverzoeken") return "Toegangsverzoeken";
  if (pathname === "/gesprekken") return "Gesprekken";
  if (pathname === "/sheets-sync") return "Sheets-synchronisatie";
  return "Waves Partnerportaal";
}

function partnerAbbr(name: string) {
  return name.split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

// ── Nav building blocks ─────────────────────────────────────

const NAV_ITEM = "flex items-center gap-3 rounded-nav p-3 text-[15px] transition-colors hover:bg-waves-mist";
const NAV_ACTIVE = "bg-waves-mist font-bold text-waves-ink";
const NAV_INACTIVE = "font-medium text-waves-body";

function CountBadge({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-waves-koraal-hover px-1.5 font-mono text-[11px] font-bold text-white">
      {n > 99 ? "99+" : n}
    </span>
  );
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  end?: boolean;
  /** Overrides NavLink's own path matching, for links whose active state depends on the query string. */
  active?: boolean;
  badge?: number;
  /** data-tour target for the help tours. */
  tour?: string;
}

function NavItem({ to, icon, label, end, active, badge = 0, tour }: NavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      data-tour={tour}
      className={({ isActive }) => cn(NAV_ITEM, (active ?? isActive) ? NAV_ACTIVE : NAV_INACTIVE)}
    >
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center [&_svg]:h-[18px] [&_svg]:w-[18px]">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <CountBadge n={badge} />
    </NavLink>
  );
}

function SubNavItem({ to, label, active }: { to: string; label: React.ReactNode; active: boolean }) {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center rounded-[10px] px-3 py-2 text-sm transition-colors hover:bg-waves-mist",
        active ? "bg-waves-mist font-bold text-waves-ink" : "font-medium text-waves-body",
      )}
    >
      <span className="truncate">{label}</span>
    </Link>
  );
}

function NavSection({ label }: { label: string }) {
  return <SectionLabel className="px-3 pb-2 pt-5">{label}</SectionLabel>;
}

function PartnerIcon({ partner }: { partner: Partner }) {
  const iconUrl = partnerIconUrl(partner);
  if (iconUrl) {
    return <img src={iconUrl} alt="" className="h-[18px] w-[18px] object-contain" />;
  }
  return (
    <span className="flex h-[18px] w-[18px] items-center justify-center rounded bg-waves-lucht font-mono text-[9px] font-bold text-waves-ink">
      {partnerAbbr(partner.name)}
    </span>
  );
}

// ── Layout ──────────────────────────────────────────────────

export default function Layout({ children, title, subtitle, actions, noPadding, bare }: LayoutProps) {
  const { user, logout } = useAuth();
  const { data: profile } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentBranch = searchParams.get("branch");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const role = user?.role;
  const isPartnerBound = role === "hmp" || role === "partner_admin";
  const isWaves = role === "admin" || role === "kam";

  const { data: partners = [] } = usePartners();
  const { data: notifications = [] } = useNotifications();
  const { data: tasksData } = useTasks();
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const taskCount = tasksData?.tasks?.length ?? 0;

  // Partner-bound roles list their own partner's branches; KAM/admin list the
  // branches of whichever partner page is currently open.
  const activePartnerId = isWaves
    ? Number(matchPath("/vacatures/:partnerId", location.pathname)?.params.partnerId) || undefined
    : undefined;
  const branchPartnerId = isPartnerBound ? user?.partner_id ?? undefined : activePartnerId;
  const { data: branches = [] } = useBranches(branchPartnerId, !!branchPartnerId);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  const { openSettings } = useSettingsDialog();
  function openOwnSettings() {
    setMobileMenuOpen(false);
    openSettings();
  }

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const visiblePartners = isWaves ? partners : partners.filter(p => p.id === user?.partner_id);
  const ownPartner = user?.partner_id ? partners.find(p => p.id === user.partner_id) : undefined;
  const pageTitle = pageTitleFromPath(location.pathname);

  const vacBranchLinks = (partnerId: number) => {
    const onPartnerPage = location.pathname === `/vacatures/${partnerId}`;
    return branches
      .filter(b => role !== "hmp" || (user?.branch_permissions ?? []).includes(b.id))
      .map(b => (
        <SubNavItem
          key={b.id}
          to={`/vacatures/${partnerId}?branch=${b.id}`}
          label={b.name}
          active={onPartnerPage && currentBranch === String(b.id)}
        />
      ));
  };

  // ── Sidebar ────────────────────────────────────────────────
  const sidebar = (isMobile: boolean) => (
    <aside
      className={cn(
        "flex h-full flex-col bg-white pb-6 pt-8",
        isMobile ? "w-full" : "w-[280px] shrink-0 border-r border-waves-line",
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-5 pb-4">
        <div className="relative flex flex-col items-center gap-2.5 text-center border-b border-waves-line px-3 pb-6">
          <button onClick={() => navigate("/")} className="block" aria-label="Naar dashboard">
            <WavesLogo className="h-8" />
          </button>
          {environment === "local" && (
            <span className="rounded-pill bg-status-alert-bg px-2.5 py-1 text-[11px] font-bold uppercase tracking-label text-status-alert-fg">
              Devomgeving
            </span>
          )}
          {environment === "test" && (
            <span className="rounded-pill bg-status-warn-bg px-2.5 py-1 text-[11px] font-bold uppercase tracking-label text-status-warn-fg">
              Testomgeving
            </span>
          )}
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute right-0 top-0 rounded-pill p-1.5 text-waves-muted hover:bg-waves-mist"
              aria-label="Menu sluiten"
            >
              <X size={22} />
            </button>
          )}
        </div>

        <nav className="flex flex-col gap-1">
          <NavSection label="Menu" />
          <NavItem to="/" end icon={<Home />} label="Dashboard" tour="menu-dash" />
          <NavItem to="/meldingen" icon={<Bell />} label="Meldingen" badge={unreadCount} tour="menu-meldingen" />
          <NavItem to="/taken" icon={<ListChecks />} label="Taken" badge={taskCount} />

          {isPartnerBound && user?.partner_id && (
            <>
              <NavSection label="Vacatures" />
              <NavItem
                to={`/vacatures/${user.partner_id}`}
                icon={<Building2 />}
                label="Alle vestigingen"
                active={location.pathname === `/vacatures/${user.partner_id}` && !currentBranch}
                tour="menu-vac"
              />
              <div className="flex flex-col gap-0.5 pl-[30px]">{vacBranchLinks(user.partner_id)}</div>
            </>
          )}

          {isWaves && (
            <>
              <NavSection label="Partners" />
              <NavItem to="/vacatures" end icon={<Building2 />} label="Alle partners" />
              {visiblePartners.map(p => {
                const isActive = p.id === activePartnerId;
                return (
                  <React.Fragment key={p.id}>
                    <NavItem
                      to={`/vacatures/${p.id}`}
                      icon={<PartnerIcon partner={p} />}
                      label={p.name}
                      active={isActive && !currentBranch}
                    />
                    {isActive && branches.length > 0 && (
                      <div className="mb-1 ml-[21px] flex flex-col gap-0.5 border-l border-waves-line pl-3">
                        {vacBranchLinks(p.id)}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </>
          )}

          {(isWaves || role === "partner_admin") && (
            <>
              <NavSection label="Admin" />
              <NavItem to="/beheer" icon={<LayoutGrid />} label="Beheer" />
              {role === "admin" && <NavItem to="/gesprekken" icon={<MessagesSquare />} label="Gesprekken" />}
              <NavItem to="/audit-trail" icon={<FileText />} label="Audit trail" />
            </>
          )}

          <div className="pt-3">
            <button type="button" data-tour="menu-inst" onClick={openOwnSettings} className={cn(NAV_ITEM, NAV_INACTIVE, "w-full text-left")}>
              <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center [&_svg]:h-[18px] [&_svg]:w-[18px]"><Settings /></span>
              <span className="min-w-0 flex-1 truncate">Instellingen</span>
            </button>
          </div>
        </nav>
      </div>

      {user && (
        <div className="mx-5 flex shrink-0 flex-col gap-4 border-t border-waves-line px-1 pt-5">
          <button type="button" onClick={openOwnSettings} className="flex items-center gap-3 rounded-nav px-2 py-1 text-left hover:bg-waves-subtle" title="Mijn instellingen">
            <UserAvatar name={user.name} pictureUrl={profile?.profile_picture_url} size={40} />
            <div className="flex min-w-0 flex-col gap-px">
              <div className="truncate text-[15px] font-bold text-waves-ink">{user.name}</div>
              <div className="truncate text-[13px] text-waves-muted">{roleSubtitle[user.role] ?? user.role}</div>
            </div>
          </button>
          <button
            onClick={logout}
            className="flex items-center justify-center gap-2.5 rounded-pill border border-waves-line p-3 text-sm font-bold text-waves-ink transition-colors hover:bg-waves-mist"
          >
            <LogOut size={18} /> Uitloggen
          </button>
        </div>
      )}
    </aside>
  );

  return (
    <div className="flex h-full overflow-hidden bg-white text-waves-ink">
      <div className="hidden h-full md:block">{sidebar(false)}</div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="absolute inset-0 bg-waves-ink/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="relative h-full w-[300px] max-w-[85vw] overflow-hidden shadow-modal">{sidebar(true)}</div>
        </div>
      )}

      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        {bare ? (
          <header className="pointer-events-none absolute inset-x-0 top-0 z-40 flex items-center justify-between px-4 pt-5 md:justify-end md:px-6">
            <button
              className="pointer-events-auto rounded-pill border border-waves-line bg-white p-2.5 text-waves-ink shadow-soft hover:bg-waves-mist md:hidden"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Menu openen"
            >
              <Menu size={20} />
            </button>
            <div className="pointer-events-auto flex items-center gap-3">
              <div data-tour="bells" className="flex items-center gap-3">
                <NotificationBell />
                <TasksBell />
              </div>
              <HelpButton />
            </div>
          </header>
        ) : (
          <header className="flex shrink-0 items-center justify-between gap-4 px-4 pb-4 pt-5 md:px-10 md:pt-6">
            <div className="flex min-w-0 items-center gap-4">
              <button
                className="-ml-1 shrink-0 rounded-pill p-1.5 text-waves-ink hover:bg-waves-mist md:hidden"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Menu openen"
              >
                <Menu size={24} />
              </button>
              {isPartnerBound && ownPartner?.logo_url && (
                <>
                  <img src={ownPartner.logo_url} alt={ownPartner.name} className="hidden h-8 w-auto shrink-0 sm:block" />
                  <div className="hidden h-[18px] w-px shrink-0 bg-waves-line sm:block" />
                </>
              )}
              <div className="min-w-0">
                <h1 className="truncate text-2xl font-black leading-tight tracking-[-0.02em] md:text-[28px]">
                  {title ?? pageTitle}
                </h1>
                {subtitle && <div className="mt-0.5 truncate text-sm text-waves-muted">{subtitle}</div>}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {actions && <div className="flex items-center gap-2.5">{actions}</div>}
              <div data-tour="bells" className="flex items-center gap-3">
                <NotificationBell />
                <TasksBell />
              </div>
              <HelpButton />
            </div>
          </header>
        )}

        <main
          className={cn(
            "min-h-0 flex-1",
            bare ? "overflow-auto" : noPadding ? "overflow-hidden" : "overflow-auto px-4 pb-10 md:px-10",
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
